import React, { useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Grid,
  Avatar,
  Button,
  Link as MuiLink,
} from "@mui/material";
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaCheckCircle,
  FaRocket,
  FaLaptopCode,
  FaUsers,
  FaSchool,
  FaDownload,
  FaWhatsapp,
  FaFileAlt,
  FaWifi,
} from "react-icons/fa";
import logo from "../assets/mainlogo.png";

// ─── Shared section heading ───────────────────────────────────────────────────
function SectionHeading({
  label,
  color = "#0288D1",
  bg = "rgba(2,136,209,0.1)",
  border = "rgba(2,136,209,0.2)",
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
      <Box
        sx={{
          width: 4,
          height: 28,
          borderRadius: 99,
          background: color,
          flexShrink: 0,
        }}
      />
      <Chip
        label={label}
        size="small"
        sx={{
          fontWeight: 800,
          fontSize: "0.82rem",
          background: bg,
          color,
          border: `1px solid ${border}`,
          letterSpacing: "0.2px",
        }}
      />
    </Stack>
  );
}

// ─── Contact row ─────────────────────────────────────────────────────────────
function ContactRow({
  icon,
  children,
  color = "#0288D1",
  bg = "rgba(2,136,209,0.08)",
  border = "rgba(2,136,209,0.18)",
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          background: bg,
          border: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>{children}</Box>
    </Stack>
  );
}

const LiteUpperProposalPage = () => {
  const proposalRef = useRef();

  const handleDownloadPDF = async () => {
    const element = proposalRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save("LiteUpper_Proposal.pdf");
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Ambient orbs */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <Box
            sx={{
              position: "absolute",
              width: 600,
              height: 600,
              borderRadius: "50%",
              top: -150,
              left: -120,
              background: "rgba(2,136,209,0.07)",
              filter: "blur(100px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 400,
              height: 400,
              borderRadius: "50%",
              bottom: -80,
              right: -80,
              background: "rgba(124,58,237,0.06)",
              filter: "blur(80px)",
            }}
          />
        </Box>
        {/* Dot grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.35,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, rgba(2,136,209,0.2) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <Container
          maxWidth="md"
          sx={{ position: "relative", zIndex: 1, py: { xs: 5, md: 8 } }}
        >
          {/* ══════════════════════════════════════════════════════════════
              PROPOSAL CARD
          ══════════════════════════════════════════════════════════════ */}
          <Paper
            ref={proposalRef}
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(2,136,209,0.15)",
              boxShadow:
                "0 24px 80px rgba(2,136,209,0.10), 0 4px 24px rgba(0,0,0,0.06)",
              overflow: "hidden",
              background: "rgba(255,255,255,0.88)",
              backdropFilter: "blur(20px)",
              position: "relative",
            }}
          >
            {/* Watermark */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%) rotate(-45deg)",
                opacity: 0.03,
                pointerEvents: "none",
              }}
            >
              <Box component="img" src={logo} alt="" sx={{ width: 500 }} />
            </Box>

            {/* ── Rainbow accent bar ────────────────────────────────── */}
            <Box
              sx={{
                height: 3,
                background: "linear-gradient(90deg, #0288D1, #0ea5e9, #7c3aed)",
              }}
            />

            {/* ── Hero header ──────────────────────────────────────── */}
            <Box
              sx={{
                background:
                  "linear-gradient(135deg, #0288D1 0%, #0ea5e9 55%, #7c3aed 100%)",
                px: { xs: 3, md: 6 },
                py: { xs: 4, md: 5 },
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.05,
                  backgroundImage:
                    "radial-gradient(circle, #fff 1px, transparent 1px)",
                  backgroundSize: "28px 28px",
                  pointerEvents: "none",
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Avatar
                  src={logo}
                  alt="LiteUpper"
                  sx={{
                    width: 96,
                    height: 96,
                    mx: "auto",
                    mb: 2.5,
                    bgcolor: "#fff",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    border: "3px solid rgba(255,255,255,0.3)",
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: "#fff",
                    letterSpacing: "-1px",
                    mb: 0.5,
                  }}
                >
                  LITEUPPER
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.78)",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}
                >
                  Empowering Education Through Technology
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  sx={{ mt: 2.5 }}
                >
                  {["Free Platform", "CBT Ready", "JAMB · WAEC · NECO"].map(
                    (tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          background: "rgba(255,255,255,0.15)",
                          color: "#fff",
                          border: "1px solid rgba(255,255,255,0.25)",
                          backdropFilter: "blur(8px)",
                        }}
                      />
                    )
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 4, md: 5 } }}>
              {/* ── Contact info card ──────────────────────────────── */}
              <Paper
                elevation={0}
                sx={{
                  mb: 5,
                  borderRadius: 3,
                  border: "1px solid rgba(2,136,209,0.15)",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid rgba(2,136,209,0.12)",
                    background:
                      "linear-gradient(135deg, rgba(2,136,209,0.06), rgba(14,165,233,0.03))",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg,#0288D1,#0ea5e9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
                    }}
                  >
                    <FaFileAlt size={14} color="#fff" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "#0c1a2e",
                        fontSize: "0.92rem",
                      }}
                    >
                      Company Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      LiteUpper Technologies
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto !important" }}>
                    <Chip
                      label="8th November, 2025"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        background: "rgba(2,136,209,0.1)",
                        color: "#0288D1",
                        border: "1px solid rgba(2,136,209,0.2)",
                      }}
                    />
                  </Box>
                </Stack>
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: <FaMapMarkerAlt size={14} />,
                        content: (
                          <Typography
                            sx={{ color: "#64748b", fontSize: "0.87rem" }}
                          >
                            3B Christ Alli Crescent, Ikoyi
                          </Typography>
                        ),
                      },
                      {
                        icon: <FaEnvelope size={14} />,
                        content: (
                          <MuiLink
                            href="mailto:liteupper@gmail.com"
                            underline="none"
                            sx={{
                              color: "#0288D1",
                              fontWeight: 700,
                              fontSize: "0.87rem",
                              "&:hover": { color: "#0277bd" },
                            }}
                          >
                            liteupper@gmail.com
                          </MuiLink>
                        ),
                      },
                      {
                        icon: <FaPhone size={14} />,
                        content: (
                          <Typography
                            sx={{ color: "#64748b", fontSize: "0.87rem" }}
                          >
                            09061709128 · 09048993633
                          </Typography>
                        ),
                      },
                      {
                        icon: <FaGlobe size={14} />,
                        content: (
                          <MuiLink
                            href="https://www.liteupper.com"
                            target="_blank"
                            underline="none"
                            sx={{
                              color: "#0288D1",
                              fontWeight: 700,
                              fontSize: "0.87rem",
                              "&:hover": { color: "#0277bd" },
                            }}
                          >
                            www.liteupper.com
                          </MuiLink>
                        ),
                      },
                    ].map((row, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <ContactRow icon={row.icon}>{row.content}</ContactRow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>

              {/* ── Subject ────────────────────────────────────────── */}
              <Box
                sx={{
                  mb: 4,
                  px: 3,
                  py: 2.5,
                  borderRadius: 3,
                  background: "rgba(124,58,237,0.05)",
                  border: "1px solid rgba(124,58,237,0.15)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#7c3aed",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    fontSize: "0.68rem",
                    display: "block",
                    mb: 0.8,
                  }}
                >
                  Subject
                </Typography>
                <Typography
                  sx={{
                    color: "#0c1a2e",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    fontStyle: "italic",
                    lineHeight: 1.65,
                  }}
                >
                  Proposal for a Free Digital Learning Solution to Enhance
                  Examination Readiness and Academic Performance
                </Typography>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Introduction ───────────────────────────────────── */}
              <Box sx={{ mb: 5 }}>
                <SectionHeading label="Introduction" />
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.8,
                    mb: 1.5,
                  }}
                >
                  Dear (Recipient's Name or Sir/Ma),
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.8,
                    mb: 1.5,
                  }}
                >
                  We are pleased to present this proposal from
                  <Box
                    component="span"
                    sx={{ fontWeight: 800, color: "#0c1a2e", mx: 2 }}
                  >
                    LiteUpper
                  </Box>
                  a technology-driven educational company committed to
                  transforming the way schools, teachers, and students prepare
                  for examinations and manage academic performance across
                  Nigeria and beyond. LiteUpper is more than a digital learning
                  platform — it is a complete CBT-centered educational solution
                  designed to help institutions conduct computer-based tests,
                  manage examinations efficiently, monitor student performance,
                  and improve academic outcomes through technology.
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.8,
                  }}
                >
                  Our platform provides students with access to verified WAEC,
                  JAMB, NECO, GCE, and Post-UTME past questions, while giving
                  schools powerful tools for class analysis, student reports,
                  score tracking, academic performance monitoring, and secure
                  computer-based testing. In addition, LiteUpper supports both
                  online and offline examination modes, allowing institutions to
                  conduct CBT examinations even in environments with unstable or
                  limited internet connectivity. Our mission is simple yet
                  powerful — to make quality learning, examination preparation,
                  and digital academic management accessible, effective, secure,
                  and affordable for every institution and student.
                </Typography>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Overview ───────────────────────────────────────── */}
              <Box sx={{ mb: 5 }}>
                <SectionHeading
                  label="Overview of the Project"
                  color="#7c3aed"
                  bg="rgba(124,58,237,0.08)"
                  border="rgba(124,58,237,0.2)"
                />
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  LiteUpper has developed a modern CBT and digital learning
                  platform designed to help schools, teachers, and students
                  seamlessly manage examinations, academic records, and learning
                  resources in one centralized system. The platform supports
                  both online and offline examination modes, enabling schools to
                  conduct secure CBT examinations with or without internet
                  access while ensuring reliable result synchronization and
                  performance tracking.
                </Typography>
                <Grid container spacing={2}>
                  {[
                    "Termly Examination Questions for SS1–SS3",
                    "CBT Examination System for objective and theory-based assessments",
                    "Offline CBT Exams",
                    "Objective and Theory Questions with detailed marking guides",
                    "Verified Past Questions for JAMB, WAEC, NECO, GCE, Post-UTME",
                  ].map((item, i) => (
                    <Grid item xs={12} md={4} key={i}>
                      <Box
                        sx={{
                          px: 2,
                          py: 1.8,
                          borderRadius: 2.5,
                          background: "rgba(124,58,237,0.05)",
                          border: "1px solid rgba(124,58,237,0.15)",
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ mt: 0.3, flexShrink: 0 }}>
                          <FaCheckCircle size={14} color="#7c3aed" />
                        </Box>
                        <Typography
                          sx={{
                            color: "#0c1a2e",
                            fontWeight: 600,
                            fontSize: "0.84rem",
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Objectives ─────────────────────────────────────── */}
              <Box sx={{ mb: 5 }}>
                <SectionHeading
                  label="Objectives of the Platform"
                  color="#059669"
                  bg="rgba(5,150,105,0.08)"
                  border="rgba(5,150,105,0.2)"
                />
                <Stack spacing={1.5}>
                  {[
                    "Provide students with free, easy access to verified materials.",
                    "Help teachers and schools save time in exam preparation.",
                    "Create a digital exam management and record-keeping system.",
                    "Encourage self-assessment and confidence building.",
                    "Promote digital literacy and better exam performance.",
                  ].map((obj, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{
                        px: 2.5,
                        py: 1.8,
                        borderRadius: 2.5,
                        background: "rgba(5,150,105,0.04)",
                        border: "1px solid rgba(5,150,105,0.12)",
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "rgba(5,150,105,0.08)",
                          borderColor: "rgba(5,150,105,0.22)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          background: "rgba(5,150,105,0.12)",
                          border: "1px solid rgba(5,150,105,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <FaRocket size={11} color="#059669" />
                      </Box>
                      <Typography
                        sx={{
                          color: "#0c1a2e",
                          fontSize: "0.88rem",
                          fontWeight: 600,
                          lineHeight: 1.6,
                          pt: 0.2,
                        }}
                      >
                        {obj}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Key Features ───────────────────────────────────── */}
              <Box sx={{ mb: 5 }}>
                <SectionHeading label="Key Features of the App" />
                <Grid container spacing={2}>
                  {[
                    {
                      icon: <FaLaptopCode size={18} />,
                      title: "Exam Management",
                      text: "Create, schedule, and manage digital exams with ease.",
                      color: "#0288D1",
                      bg: "rgba(2,136,209,0.08)",
                      border: "rgba(2,136,209,0.18)",
                    },
                    {
                      icon: <FaWifi size={18} />,
                      title: "Offline CBT Exams",
                      text: "Conduct secure computer-based examinations without internet connectivity with automatic sync when reconnected.",
                      color: "#dc2626",
                      bg: "rgba(220,38,38,0.08)",
                      border: "rgba(220,38,38,0.18)",
                    },
                    {
                      icon: <FaUsers size={18} />,
                      title: "Score & Reports",
                      text: "Automatic score storage and detailed progress reports.",
                      color: "#7c3aed",
                      bg: "rgba(124,58,237,0.08)",
                      border: "rgba(124,58,237,0.18)",
                    },
                    {
                      icon: <FaCheckCircle size={18} />,
                      title: "Analytics",
                      text: "Real-time analytics and performance insights per student.",
                      color: "#059669",
                      bg: "rgba(5,150,105,0.08)",
                      border: "rgba(5,150,105,0.18)",
                    },
                    {
                      icon: <FaSchool size={18} />,
                      title: "Teacher Tools",
                      text: "Question upload, theory marking and class management.",
                      color: "#d97706",
                      bg: "rgba(217,119,6,0.08)",
                      border: "rgba(217,119,6,0.18)",
                    },
                    {
                      icon: <FaGlobe size={18} />,
                      title: "Web & Mobile",
                      text: "Centralised dashboard accessible on web and mobile.",
                      color: "#0288D1",
                      bg: "rgba(2,136,209,0.08)",
                      border: "rgba(2,136,209,0.18)",
                    },
                    {
                      icon: <FaDownload size={18} />,
                      title: "Offline Access",
                      text: "Offline support and reduced dependency on paper.",
                      color: "#059669",
                      bg: "rgba(5,150,105,0.08)",
                      border: "rgba(5,150,105,0.18)",
                    },
                  ].map((f, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box
                        sx={{
                          px: 2.5,
                          py: 2.2,
                          borderRadius: 2.5,
                          background: f.bg,
                          border: `1px solid ${f.border}`,
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: `0 6px 20px ${f.border}`,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            color: f.color,
                          }}
                        >
                          {f.icon}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#0c1a2e",
                              fontSize: "0.88rem",
                              mb: 0.3,
                            }}
                          >
                            {f.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#64748b",
                              fontSize: "0.83rem",
                              lineHeight: 1.6,
                            }}
                          >
                            {f.text}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Expected Impact ────────────────────────────────── */}
              <Box sx={{ mb: 5 }}>
                <SectionHeading
                  label="Expected Impact"
                  color="#d97706"
                  bg="rgba(217,119,6,0.08)"
                  border="rgba(217,119,6,0.2)"
                />
                <Grid container spacing={1.8}>
                  {[
                    "Improved academic performance through regular practice.",
                    "Reduced failure rates in national examinations.",
                    "Increased adoption of digital tools in schools.",
                    "Support for teachers in managing exams efficiently.",
                    "Boosted student confidence and motivation.",
                  ].map((item, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                        sx={{
                          px: 2,
                          py: 1.6,
                          borderRadius: 2,
                          background: "rgba(217,119,6,0.04)",
                          border: "1px solid rgba(217,119,6,0.12)",
                        }}
                      >
                        <FaCheckCircle
                          size={14}
                          color="#d97706"
                          style={{ marginTop: 3, flexShrink: 0 }}
                        />
                        <Typography
                          sx={{
                            color: "#0c1a2e",
                            fontSize: "0.86rem",
                            fontWeight: 600,
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ mb: 4, borderColor: "rgba(2,136,209,0.12)" }} />

              {/* ── Commitment ─────────────────────────────────────── */}
              <Box sx={{ mb: 4 }}>
                <SectionHeading
                  label="Our Commitment"
                  color="#7c3aed"
                  bg="rgba(124,58,237,0.08)"
                  border="rgba(124,58,237,0.2)"
                />
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    borderRadius: 3,
                    background: "rgba(124,58,237,0.04)",
                    border: "1px solid rgba(124,58,237,0.12)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: "0.92rem",
                      lineHeight: 1.85,
                    }}
                  >
                    At LiteUpper, our commitment is to bridge the gap between
                    students and quality education by providing reliable,
                    accessible, and innovative digital learning solutions for
                    schools and institutions. We are committed to helping
                    schools transition into a modern CBT-driven educational
                    environment where examinations, academic records, student
                    reports, and class analysis can be managed efficiently and
                    securely. We are also committed to providing reliable
                    offline CBT examination capabilities for schools in areas
                    with limited or unstable internet access, ensuring
                    uninterrupted examinations and continuous academic
                    activities.
                  </Typography>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: "0.92rem",
                      lineHeight: 1.8,
                    }}
                  >
                    Through our platform, institutions can monitor student
                    progress in real time, identify academic strengths and
                    weaknesses through detailed analytics, and provide students
                    with continuous practice using verified WAEC and other
                    national examination past questions. Our goal is to empower
                    schools with technology that improves learning outcomes,
                    enhances examination readiness, and simplifies academic
                    management — all while remaining affordable and accessible.
                  </Typography>
                </Box>
              </Box>

              {/* ── Conclusion ─────────────────────────────────────── */}
              <Box sx={{ mb: 6 }}>
                <SectionHeading label="Conclusion" />
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.85,
                    mb: 1.5,
                  }}
                >
                  Education remains the foundation of national growth and
                  development. At LiteUpper, we believe technology should
                  simplify learning, strengthen examination preparation, and
                  improve the way schools manage academic performance. Our
                  CBT-centered platform empowers institutions with modern tools
                  for conducting examinations, analyzing student and class
                  performance, generating academic reports, and preparing
                  students effectively for WAEC, JAMB, NECO, and other national
                  examinations.
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.8,
                  }}
                >
                  By combining digital examinations, academic analytics, and
                  verified past questions into one unified system, LiteUpper
                  provides schools with a smarter and more efficient approach to
                  education management. We would be honoured to demonstrate the
                  LiteUpper platform and discuss how your institution can begin
                  using it to improve examination management and student
                  performance.
                </Typography>
              </Box>

              {/* ── Signature block ────────────────────────────────── */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(2,136,209,0.15)",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid rgba(2,136,209,0.12)",
                    background:
                      "linear-gradient(135deg, rgba(2,136,209,0.06), rgba(14,165,233,0.03))",
                  }}
                >
                  <Avatar
                    src={logo}
                    sx={{
                      width: 40,
                      height: 40,
                      border: "2px solid rgba(2,136,209,0.2)",
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: "#0c1a2e",
                        fontSize: "1rem",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      LiteUpper Team
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      Sincerely yours
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Grid container spacing={2}>
                    {[
                      {
                        icon: <FaWhatsapp size={14} />,
                        color: "#059669",
                        bg: "rgba(5,150,105,0.08)",
                        border: "rgba(5,150,105,0.18)",
                        content: (
                          <MuiLink
                            href="https://wa.me/2349061709128"
                            target="_blank"
                            underline="none"
                            sx={{
                              color: "#059669",
                              fontWeight: 700,
                              fontSize: "0.87rem",
                              "&:hover": { color: "#047857" },
                            }}
                          >
                            09061709128
                          </MuiLink>
                        ),
                      },
                      {
                        icon: <FaPhone size={14} />,
                        color: "#0288D1",
                        bg: "rgba(2,136,209,0.08)",
                        border: "rgba(2,136,209,0.18)",
                        content: (
                          <Typography
                            sx={{ color: "#64748b", fontSize: "0.87rem" }}
                          >
                            09048993633
                          </Typography>
                        ),
                      },
                      {
                        icon: <FaEnvelope size={14} />,
                        color: "#0288D1",
                        bg: "rgba(2,136,209,0.08)",
                        border: "rgba(2,136,209,0.18)",
                        content: (
                          <MuiLink
                            href="mailto:liteupper@gmail.com"
                            underline="none"
                            sx={{
                              color: "#0288D1",
                              fontWeight: 700,
                              fontSize: "0.87rem",
                              "&:hover": { color: "#0277bd" },
                            }}
                          >
                            liteupper@gmail.com
                          </MuiLink>
                        ),
                      },
                      {
                        icon: <FaGlobe size={14} />,
                        color: "#7c3aed",
                        bg: "rgba(124,58,237,0.08)",
                        border: "rgba(124,58,237,0.18)",
                        content: (
                          <MuiLink
                            href="https://www.liteupper.com"
                            target="_blank"
                            underline="none"
                            sx={{
                              color: "#7c3aed",
                              fontWeight: 700,
                              fontSize: "0.87rem",
                              "&:hover": { color: "#6d28d9" },
                            }}
                          >
                            www.liteupper.com
                          </MuiLink>
                        ),
                      },
                    ].map((row, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <ContactRow
                          icon={row.icon}
                          color={row.color}
                          bg={row.bg}
                          border={row.border}
                        >
                          {row.content}
                        </ContactRow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Box>
          </Paper>

          {/* ── Download button ──────────────────────────────────────── */}
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<FaDownload size={16} />}
              onClick={handleDownloadPDF}
              sx={{
                px: 5,
                py: 1.8,
                borderRadius: 99,
                fontWeight: 800,
                fontSize: "1rem",
                textTransform: "none",
                background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                boxShadow: "0 8px 28px rgba(2,136,209,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0277bd, #0288D1)",
                  boxShadow: "0 12px 36px rgba(2,136,209,0.45)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.25s",
              }}
            >
              Download Proposal PDF
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LiteUpperProposalPage;
